(() => {
  if (!customElements.get('reise-home-look')) {
    class ReiseHomeLook extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-look-grid]');
        if (!this.grid) return;
        this.measure = () => {
          if (window.matchMedia('(max-width: 767px)').matches) {
            this.style.removeProperty('--reise-look-image-height');
            return;
          }
          const cards = Array.from(this.grid.children);
          const anchor = cards[Math.floor((cards.length - 1) / 3) * 3];
          const swatch = anchor?.querySelector('span[title]') || anchor?.querySelector('.rn-card__swatches');
          if (!swatch) return;
          const height = Math.round(swatch.getBoundingClientRect().bottom - this.grid.getBoundingClientRect().top);
          if (height > 0) this.style.setProperty('--reise-look-image-height', `${height}px`);
        };
        this.observer = new ResizeObserver(this.measure);
        this.observer.observe(this.grid);
        this.grid.addEventListener('load', this.measure, true);
        window.addEventListener('resize', this.measure, { passive: true });
        this.measure();
      }
      disconnectedCallback() {
        this.observer?.disconnect();
        this.grid?.removeEventListener('load', this.measure, true);
        window.removeEventListener('resize', this.measure);
      }
    }
    customElements.define('reise-home-look', ReiseHomeLook);
  }
  if (!customElements.get('reise-home-best')) {
    class ReiseHomeBest extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-best-track]');
        this.rail = this.querySelector('[data-best-rail]');
        if (!this.track || !this.rail) return;
        this.controller = new AbortController();
        const signal = this.controller.signal;
        this.track.querySelectorAll('[data-best-duplicate] a, [data-best-duplicate] button, [data-best-duplicate] input').forEach(element => element.tabIndex = -1);
        this.track.setAttribute('tabindex', '0');
        this.track.setAttribute('role', 'region');
        this.track.setAttribute('aria-roledescription', 'carrossel');
        this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.hover = window.matchMedia('(hover: hover)');
        this.paused = false;
        this.touching = false;
        this.pointerHeld = false;
        this.visible = false;
        this.pause = () => {
          clearTimeout(this.resumeTimer);
          this.paused = true;
          this.update();
        };
        this.resume = () => {
          clearTimeout(this.resumeTimer);
          this.resumeTimer = setTimeout(() => {
            this.paused = this.touching || this.pointerHeld || (this.hover.matches && this.track.matches(':hover')) || this.track.contains(document.activeElement);
            this.update();
          }, 600);
        };
        this.track.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') this.pause(); }, { signal });
        this.track.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse') this.resume(); }, { signal });
        this.track.addEventListener('pointerdown', event => {
          if (event.pointerType === 'touch') return;
          this.pointerHeld = true;
          this.pause();
        }, { passive: true, signal });
        const releasePointer = event => {
          if (event.pointerType === 'touch') return;
          this.pointerHeld = false;
          this.resume();
        };
        window.addEventListener('pointerup', releasePointer, { passive: true, signal });
        window.addEventListener('pointercancel', releasePointer, { passive: true, signal });
        this.track.addEventListener('touchstart', () => { this.touching = true; this.pause(); }, { passive: true, signal });
        const releaseTouch = () => { this.touching = false; this.resume(); };
        window.addEventListener('touchend', releaseTouch, { passive: true, signal });
        window.addEventListener('touchcancel', releaseTouch, { passive: true, signal });
        this.track.addEventListener('wheel', () => { this.pause(); this.resume(); }, { passive: true, signal });
        this.track.addEventListener('scroll', () => { if (!this.animation) this.resume(); }, { passive: true, signal });
        this.track.addEventListener('focusin', event => {
          this.pause();
          event.target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
        }, { signal });
        this.track.addEventListener('focusout', this.resume, { signal });
        this.observe = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.update();
        });
        this.observe.observe(this.track);
        this.update = () => {
          if (!this.visible || this.motion.matches || this.paused || !this.canLoop || document.hidden) {
            this.stopAnimation();
            return;
          }
          if (this.animation) return;
          const position = this.track.scrollLeft % this.loopWidth;
          this.rail.classList.add('is-autoplaying');
          this.animation = this.rail.animate([
            { transform: 'translate3d(0, 0, 0)' },
            { transform: `translate3d(-${this.loopWidth}px, 0, 0)` }
          ], { duration: this.loopWidth / .03, iterations: Infinity, easing: 'linear' });
          this.animation.currentTime = position / .03;
          this.track.scrollLeft = 0;
        };
        this.measure = () => {
          this.stopAnimation();
          const originals = [...this.rail.children].filter(element => !element.hasAttribute('data-best-duplicate'));
          const first = originals[0];
          const last = originals.at(-1);
          const contentWidth = first ? last.getBoundingClientRect().right - first.getBoundingClientRect().left : 0;
          this.loopWidth = contentWidth + (parseFloat(getComputedStyle(this.rail).columnGap) || 0);
          this.canLoop = contentWidth > this.track.clientWidth + 1;
          this.track.querySelectorAll('[data-best-duplicate]').forEach(element => { element.hidden = !this.canLoop; });
          this.update();
        };
        this.resizeObserver = new ResizeObserver(this.measure);
        this.resizeObserver.observe(this.track);
        this.motion.addEventListener('change', this.measure, { signal });
        document.addEventListener('visibilitychange', this.update, { signal });
        this.measure();
      }
      stopAnimation() {
        if (!this.animation) return;
        const position = (Number(this.animation.currentTime) * .03) % this.loopWidth;
        this.animation.cancel();
        this.animation = null;
        this.rail.classList.remove('is-autoplaying');
        this.track.scrollLeft = position;
      }
      disconnectedCallback() {
        this.stopAnimation();
        clearTimeout(this.resumeTimer);
        this.observe?.disconnect();
        this.resizeObserver?.disconnect();
        this.controller?.abort();
      }
    }
    customElements.define('reise-home-best', ReiseHomeBest);
  }
})();
